'use client';

import React, { useState } from 'react';
import { useParams } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faHospital, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { CircularProgress } from 'src/components';
import { SiteBySpecialtyItem } from './components';
import { useGetSystemSitesBySpecialtyQuery } from 'src/api/redux';

const SitesBySpecialty = ({ systemId }: { systemId?: string }) => {
  const { id } = useParams<{ id: string }>();

  const [sortProps, setSortProps] = useState({
    sort: 'count',
    order: 'desc',
  });

  const systemIdToUse = id || systemId || null;

  const {
    currentData: sitesBySpecialtyData,
    isLoading,
    isFetching,
  } = useGetSystemSitesBySpecialtyQuery(
    {
      systemId: systemIdToUse,
      ...sortProps,
    },
    { skip: !systemIdToUse }
  );

  const handleSortChange = () => {
    setSortProps((prev) => ({
      ...prev,
      order: prev.order === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div
      data-testid="sites-by-specialty-container"
      className="h-full flex flex-col p-6 bg-white w-full border border-neutral-200 rounded-lg relative"
    >
      <div className="self-stretch flex flex-col justify-center items-start gap-1">
        <div className="inline-flex items-center gap-1.5">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 flex items-center justify-center">
              <Icon
                icon={faHospital}
                size="lg"
                className="w-5 h-5 text-secondary"
              />
            </div>
            <div
              data-testid="sites-by-specialty-title"
              className="text-primary text-lg font-bold font-['Inter'] leading-7"
            >
              Sites by Specialty
            </div>
          </div>
        </div>
      </div>

      <div className="inline-flex items-start w-full justify-between h-14 px-2 py-1.5 bg-white border-b border-neutral-200">
        <div className="self-stretch inline-flex justify-between items-center overflow-hidden">
          <div className="flex-1 text-black text-sm font-semibold font-['Inter'] leading-tight">
            Specialty
          </div>
        </div>
        <div
          data-testid="sort-button"
          className="self-stretch inline-flex justify-between items-center gap-2 
          overflow-hidden rounded-md px-1 cursor-pointer hover:bg-neutral-100"
          onClick={handleSortChange}
        >
          <div className="text-right text-black text-sm font-semibold font-['Inter'] leading-tight">
            # of Sites
          </div>
          <div className="w-5 h-5 flex items-center justify-center">
            <Icon
              icon={faChevronDown}
              className={`w-3 h-1.5 text-secondary 
                  transition-transform duration-300 ${
                    sortProps.order === 'asc' ? '-rotate-180' : 'rotate-0'
                  }`}
            />
          </div>
        </div>
      </div>
      <div
        data-testid="specialty-items-container"
        className="max-h-80 flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-3xl"
      >
        {sitesBySpecialtyData?.results?.length > 0 ? (
          sitesBySpecialtyData.results.map((speciality, index) => {
            return (
              <SiteBySpecialtyItem
                key={`${speciality.specialty}-${index}`}
                specialty={speciality}
              />
            );
          })
        ) : !isLoading && !isFetching ? (
          <div
            data-testid="empty-state"
            className="flex flex-col items-center justify-center h-full min-h-48 text-center px-4 gap-3"
          >
            <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
              <Icon icon={faHospital} className="w-6 h-6 text-neutral-400" />
            </div>
            <div className="flex flex-col gap-1">
              <h3
                data-testid="empty-state-title"
                className="text-sm font-semibold text-neutral-900"
              >
                No specialties found
              </h3>
              <p
                data-testid="empty-state-description"
                className="text-xs text-neutral-500 max-w-xs"
              >
                There are no site specialties available for this system.
              </p>
            </div>
          </div>
        ) : null}
      </div>
      {(isLoading || isFetching) && (
        <div
          data-testid="loading-overlay"
          className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center rounded-lg z-10"
        >
          <CircularProgress size="sm" color="neutral" />
        </div>
      )}
    </div>
  );
};

export default SitesBySpecialty;
